// The client half of `/masuk`: reads the `lanjut` query parameter `middleware.ts` sets when it
// redirects an unauthenticated visitor here, and lands the visitor back where they were headed
// after a successful sign-in. Split from `page.tsx` because `useSearchParams` needs a `Suspense`
// boundary above it (Next.js docs) — `page.tsx` provides that boundary as a plain Server Component.
'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
// Relative, not `@/` — `frontend/vitest.config.ts` (T-03's, frozen) has no path-alias resolution.
import { NoticeBand } from '../../../components/ui/NoticeBand';
import { SignInForm, useInvalidateSession } from '../../../features/auth';

// Landing page paska-login investor: langsung menuju Executive Dashboard Portofolio (/portofolio).
const DEFAULT_DESTINATION = '/portofolio';

// A fixed, non-network placeholder origin. Resolving `raw` against it and checking that the
// resolved `origin` is unchanged is the only reliable way to tell a same-app path from one that
// escapes it — string-prefix checks (reject `//`, reject a leading `\`, …) are a losing game
// against a URL parser's own normalisation (docs/qa/reports/T-19-r1.md F-2: `/\evil.example`
// slips past a `//`-only check because the WHATWG parser folds a leading backslash into a slash
// for a special scheme, and Next's router then treats the result as an external URL and performs
// a real hard navigation off-origin).
const PLACEHOLDER_ORIGIN = 'http://x.invalid';

/** Only a same-app, root-relative path is accepted; anything the URL parser resolves to a
 * different origin (an absolute URL, `//host/...`, `/\host/...`, `/\/host/...`, …) falls back to
 * the default rather than sending the browser somewhere `middleware.ts` never intended `lanjut`
 * to point at.
 *
 * There are two checks below because two different values are at stake, and the second one is the
 * whole lesson of docs/qa/reports/T-19-r2.md F-11: **validate the value you are about to return,
 * not only the value you were given.** The origin comparison proves what `raw` means. It does not
 * prove what the reconstructed `pathname + search + hash` means, and that reconstruction — not
 * `raw` — is what reaches `router.push()`. Dot-segment normalisation happens between the two:
 * `/..//evil.example` resolves to `http://x.invalid//evil.example`, whose origin is unchanged (so
 * the first check passes) but whose `pathname` is `//evil.example`. Next then re-resolves that
 * returned string against the real page origin (`new URL(dest, location.href)`), reads a
 * protocol-relative path as `https://evil.example/`, finds it off-origin and performs a full hard
 * navigation — off a genuine sign-in on the genuine domain. A resolved path can only begin with
 * `//` by way of that normalisation, never from a legitimate `lanjut`, so rejecting it costs
 * nothing and closes the family (14 payloads escaped without it). */
function safeDestination(raw: string | null): string {
  if (!raw) {
    return DEFAULT_DESTINATION;
  }
  let resolved: URL;
  try {
    resolved = new URL(raw, PLACEHOLDER_ORIGIN);
  } catch {
    return DEFAULT_DESTINATION;
  }
  if (resolved.origin !== PLACEHOLDER_ORIGIN || resolved.pathname.startsWith('//')) {
    return DEFAULT_DESTINATION;
  }
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}

export function SignInPanel() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const invalidateSession = useInvalidateSession();

  // `SignInForm.onSuccess` passes the newly authenticated user; this page only needs to know that
  // it happened, so the callback below takes no parameter (a function of fewer arguments than the
  // prop's type is assignable, per TypeScript's structural typing of function types).
  const handleSuccess = useCallback(() => {
    invalidateSession();
    router.push(safeDestination(searchParams.get('lanjut')));
  }, [invalidateSession, router, searchParams]);

  return (
    // The submit button's clay fill, size and label come from `.auth-canvas button[type='submit']`
    // (`styles/auth-canvas.css`), so `SignInForm` and `components/ui/Button` stay untouched.
    <div className="flex w-full flex-col gap-s5">
      <h1 style={{ marginBottom: 0 }}>Masuk</h1>
      {searchParams.get('sesi') === 'berakhir' ? (
        <NoticeBand tone="info" title="Sesi berakhir">
          Sesi Anda sudah berakhir. Masuk kembali untuk melanjutkan.
        </NoticeBand>
      ) : null}
      <SignInForm onSuccess={handleSuccess} door="investor" />
      <p className="auth-note">
        Belum punya akun?{' '}
        <Link href="/daftar" className="underline underline-offset-2">
          Daftar di sini
        </Link>
        .
      </p>
      <p className="auth-note">
        <Link href="/masuk/staf" className="underline underline-offset-2">
          Masuk sebagai staf PPA, ops, atau superadmin
        </Link>
        .
      </p>
    </div>
  );
}
